import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const OrderComplete = () => {
  const location = useLocation();
  const { orderNumber, orderData } = location.state || {};

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const formatPrice = (price) => {
    // 如果价格已经是格式化的字符串（如 "S$22.90"），直接返回
    if (typeof price === 'string') {
      return price;
    }
    // 如果价格是数字，格式化为 "S$" 格式
    return `S$${price?.toFixed(2) || '0.00'}`;
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getEstimatedDelivery = () => {
    const today = new Date();
    const deliveryDate = new Date(today);
    
    // Add delivery days based on shipping method
    switch (orderData?.shippingMethod) {
      case 'express':
        deliveryDate.setDate(today.getDate() + 2);
        break;
      case 'overnight':
        deliveryDate.setDate(today.getDate() + 1);
        break;
      default:
        deliveryDate.setDate(today.getDate() + 5);
    }
    
    return deliveryDate.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!orderNumber || !orderData) {
    return (
      <div className="min-h-screen bg-dark-400 pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-16">
            <div className="text-8xl mb-6">❌</div>
            <h2 className="text-3xl font-bold text-white mb-4">订单信息不存在</h2>
            <p className="text-xl text-gray-400 mb-8">
              无法找到订单信息，请重新下单。
            </p>
            <Link to="/shop">
              <button className="btn-primary text-lg px-8 py-4">
                继续购物
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-400 pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Animation */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
        >
          <motion.div
            className="text-8xl mb-6"
            animate={{ 
              rotate: [0, 10, -10, 0],
              scale: [1, 1.1, 1, 1.05, 1]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3
            }}
          >
            ✅
          </motion.div>
          
          <motion.h1
            className="text-4xl md:text-5xl font-futuristic font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <span className="text-white">订单确认成功！</span>
          </motion.h1>
          
          <motion.p
            className="text-xl text-gray-300 mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            您的订单已被确认，正在使用AI验证精度进行处理。
          </motion.p>
          
          <motion.p
            className="text-lg text-primary-400 font-medium"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            订单号: {orderNumber}
          </motion.p>
        </motion.div>

        {/* Order Details Card */}
        <motion.div
          className="card-glass p-8 rounded-lg mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <h2 className="text-2xl font-semibold text-white mb-6">订单详情</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Order Info */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-white mb-3">订单信息</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">订单号:</span>
                    <span className="text-white font-medium">{orderNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">下单日期:</span>
                    <span className="text-white">{getCurrentDate()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">预计送达:</span>
                    <span className="text-white">{getEstimatedDelivery()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">订单总额:</span>
                    <span className="text-white font-bold text-lg gradient-text">
                      {formatPrice(orderData.total)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div>
                <h3 className="text-lg font-medium text-white mb-3">支付信息</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">支付方式:</span>
                    <span className="text-white">
                      {orderData.paymentMethod === 'card' ? '💳 信用卡' : '💰 PayPal'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">支付状态:</span>
                    <span className="text-green-400 font-medium">✓ 已确认</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping Info */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-white mb-3">配送信息</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-400">收货人:</span>
                    <span className="text-white ml-2">
                      {orderData.firstName} {orderData.lastName}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">联系方式:</span>
                    <span className="text-white ml-2">{orderData.phone}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">邮箱:</span>
                    <span className="text-white ml-2">{orderData.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block">配送地址:</span>
                    <span className="text-white">
                      {orderData.address}<br />
                      {orderData.city}, {orderData.postalCode}<br />
                      {orderData.country}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* AI Processing Status */}
        <motion.div
          className="card-glass p-6 rounded-lg mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          <div className="flex items-center space-x-4">
            <motion.div
              className="w-12 h-12 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center"
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <span className="text-white font-bold text-xl">AI</span>
            </motion.div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-1">AI质量验证进行中</h3>
              <p className="text-gray-400 text-sm">
                我们的AI系统正在验证您的订单并优化配送路线，确保最佳配送体验。
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
              <span className="text-primary-400 text-sm font-medium">处理中</span>
            </div>
          </div>
        </motion.div>

        {/* Next Steps */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="card-glass p-6 rounded-lg text-center">
            <div className="text-4xl mb-4">📧</div>
            <h3 className="text-lg font-semibold text-white mb-2">确认邮件</h3>
            <p className="text-gray-400 text-sm">
              订单确认邮件已发送至您的邮箱，请查收详细信息。
            </p>
          </div>
          
          <div className="card-glass p-6 rounded-lg text-center">
            <div className="text-4xl mb-4">📦</div>
            <h3 className="text-lg font-semibold text-white mb-2">商品打包</h3>
            <p className="text-gray-400 text-sm">
              我们的团队正在仔细包装您的商品，确保安全送达。
            </p>
          </div>
          
          <div className="card-glass p-6 rounded-lg text-center">
            <div className="text-4xl mb-4">🚚</div>
            <h3 className="text-lg font-semibold text-white mb-2">物流跟踪</h3>
            <p className="text-gray-400 text-sm">
              商品发出后，您将收到跟踪号码以便实时查询配送状态。
            </p>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
        >
          <Link to="/shop">
            <motion.button
              className="btn-primary text-lg px-8 py-4"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              继续购物
            </motion.button>
          </Link>
          
          <Link to="/">
            <motion.button
              className="btn-outline text-lg px-8 py-4"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              返回首页
            </motion.button>
          </Link>
        </motion.div>

        {/* Customer Support */}
        <motion.div
          className="text-center mt-12 p-6 bg-gradient-to-r from-secondary-500/10 to-accent-500/10 rounded-lg border border-secondary-500/20"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
        >
          <h3 className="text-lg font-semibold text-white mb-2">需要帮助？</h3>
          <p className="text-gray-400 text-sm mb-4">
            如果您对订单有任何疑问，我们的AI客服团队24/7为您服务。
          </p>
          <div className="flex justify-center space-x-4">
            <button className="text-primary-400 hover:text-primary-300 transition-colors text-sm">
              📞 联系客服
            </button>
            <button className="text-primary-400 hover:text-primary-300 transition-colors text-sm">
              💬 在线聊天
            </button>
            <button className="text-primary-400 hover:text-primary-300 transition-colors text-sm">
              📧 发送邮件
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderComplete;
